import axios from 'axios'
import UAParser from 'ua-parser-js'
import { fetchApi } from './config'
import globalStore from 'redux/store'
import actions from 'redux/action'
import { aesCbcBase64 } from 'utils/cryoto'
import { combinedStorage } from 'utils/combinedStorage'
import { TRUE, FALSE, ENABLED } from 'constant/common'

// Normalize remove functions to accept { video_id } object like the add counterparts,
// since call sites use dynamic dispatch: fetchUpdateApi = remove ? fetchRemoveX : fetchAddX
// Normalize remove functions to accept object params matching their add counterparts,
// since call sites use dynamic dispatch: fetchUpdateApi = remove ? fetchRemoveX : fetchAddX
// Normalize path-param functions to accept object params matching their old call signatures
import {
  fetchRemoveLikeList as fetchRemoveLikeListBase,
  fetchRemoveCollectList as fetchRemoveCollectListBase,
  fetchRemoveComicCollectList as fetchRemoveComicCollectListBase,
  fetchRemoveNovelCollectList as fetchRemoveNovelCollectListBase,
  fetchComicChapterKey as fetchComicChapterKeyBase,
  fetchNovelChapterKey as fetchNovelChapterKeyBase,
  SchemaCmsPublishResult,
} from './generated'

// ─── Imports from generated needed by wrappers ───────────────────────────────
import {
  fetchVideoListBase,
  fetchVideoTagListBase,
  fetchVideoTopicListBase,
  fetchActorListBase,
  fetchProducerListBase,
  fetchProductListBase,
  fetchRegionListBase,
  fetchAdListBase,
  fetchAnnouncementBase,
  v1MemberAnnounceList,
  fetchShortListBase,
  fetchUploaderListBase,
  getLandingDomainsBase,
  fetchComicListBase,
  fetchComicChaptersBase,
  fetchNovelListBase,
  fetchNovelChaptersBase,
  fetchCooperationBase,
  fetchCmsPublishResultV2,
  fetchCmsPublishResultV4,
  fetchCmsPublishResultV7,
  postEventOholo,
} from './generated'

// ─── Direct re-exports from generated ────────────────────────────────────────
export {
  fetchRefreshToken,
  fetchVerificationCode,
  fetchRegister,
  fetchDeviceQrcode,
  fetchFindPreviousAccount,
  fetchUserInfo,
  fetchLogin,
  fetchVideoKey,
  fetchBuyVideo,
  fetchPurchaseProduct,
  fetchCountViewVideo,
  fetchAvailableOrigin,
  fetchAddLikeList,
  fetchAddCollectList,
  fetchStation,
  fetchComicViewChapter,
  fetchAddComicCollectList,
  fetchAddNovelCollectList,
  fetchNovelViewChapter,
  fetchVideoShare,
} from './generated'
export function fetchRemoveLikeList({ video_id, signal }: { video_id: number; signal?: AbortSignal }) {
  return fetchRemoveLikeListBase(video_id, signal)
}
export function fetchRemoveCollectList({ video_id, signal }: { video_id: number; signal?: AbortSignal }) {
  return fetchRemoveCollectListBase(video_id, signal)
}
export function fetchRemoveComicCollectList({ comic_id, signal }: { comic_id: number; signal?: AbortSignal }) {
  return fetchRemoveComicCollectListBase(comic_id, signal)
}
export function fetchRemoveNovelCollectList({ novel_id, signal }: { novel_id: number; signal?: AbortSignal }) {
  return fetchRemoveNovelCollectListBase(novel_id, signal)
}
export function fetchComicChapterKey({ id, signal }: { id: number; signal?: AbortSignal }) {
  return fetchComicChapterKeyBase(id, signal)
}
export function fetchNovelChapterKey({ id, signal }: { id: number; signal?: AbortSignal }) {
  return fetchNovelChapterKeyBase(id, signal)
}

// Re-export alias: fetchBuyShort is the same endpoint as fetchBuyVideo
export { fetchBuyVideo as fetchBuyShort } from './generated'

// ─── Re-export all types from generated (replaces types/api.ts) ───────────────
export type * from './generated'

// ─── Complex hand-written functions (not in spec or require custom logic) ────

const HAD_LOGON_KEY = 'lo'

export type SchemaCmsPublishResults = SchemaCmsPublishResult[]

// 遊客身份登入 — uses custom AES header and UA detection
export async function fetchTouristLogin({
  visitorId,
  inviteCode,
  apkenc,
}: { visitorId?: string; inviteCode?: string; apkenc?: string } = {}): Promise<any> {
  const superSecretHeader = await aesCbcBase64(`${visitorId}&cookie`)
  const parser = new UAParser(navigator.userAgent)
  const parserResults = parser.getResult()
  let isFirst = combinedStorage.getItem(HAD_LOGON_KEY) == null
  const params = {
    url: '/member/establish',
    data: {
      device_id: visitorId,
      device_type: 'cookie',
      invite_code: inviteCode || process.env.REACT_APP_DEFAULT_INVITE_CODE,
      os_version: parserResults.os.name + ' ' + parserResults.os.version,
      phone_device: parserResults.device.vendor + ' ' + parserResults.device.model,
      version: process.env.REACT_APP_VERSION,
      is_first: isFirst ? TRUE : FALSE,
      apkenc: apkenc,
    },
    method: 'POST' as const,
    headers: { Conection: superSecretHeader },
  }
  combinedStorage.setItem(HAD_LOGON_KEY, String(~~(Math.random() * 100000)))
  return fetchApi(params)
}

// 帳戶登出 — dispatches Redux side effect
export function fetchLogout(data: any): Promise<any> {
  globalStore.dispatch(actions.clearUser())
  return fetchApi({ url: '/api/Admin/Logout', data, method: 'POST' })
}

// 取得暫時token — endpoint not in swagger spec
export function fetchTemporaryToken(): Promise<{ expires_at: number; temp_access_token: string }> {
  return fetchApi({ url: '/member/temp-access-token', data: {}, method: 'GET' })
}

// 使用暫時token取得正式token — endpoint not in swagger spec
export function exchangeTemporaryToken(token: string): Promise<any> {
  return fetchApi({ url: '/member/exchange-token', data: { temp_access_token: token }, method: 'POST' })
}

// 獲取Content Management System Information — dynamic URL based on env vars
const LEGACY_SITE_ID = 1
export function fetchNavigationContent(): Promise<any> {
  const fn = process.env.REACT_APP_HAS_COMIC
    ? fetchCmsPublishResultV7
    : process.env.REACT_APP_HAS_SHORT
    ? fetchCmsPublishResultV4
    : fetchCmsPublishResultV2
  return fn({ site_id: LEGACY_SITE_ID })
}

// 讀取Bin檔 — dynamic URL, bypasses fetchApi
export function fetchBinFile({
  url,
  responseType,
  signal,
}: {
  url?: string
  responseType?: string
  signal?: AbortSignal
} = {}): Promise<any> {
  return axios.get(url!, { responseType: responseType as any, signal } as any)
}

// 支付方式 — endpoint /pay/type not in swagger spec
export function fetchPayTypeList({
  current = 1,
  pageSize = 500,
}: { current?: number; pageSize?: number } = {}): Promise<any> {
  return fetchApi({ url: '/pay/type', data: { current, pageSize, status: ENABLED } })
}

// ─── Wrappers around generated *Base functions ────────────────────────────────

export function fetchVideoList({
  signal,
  current = 1,
  pageSize = 500,
  ...rest
}: Partial<Parameters<typeof fetchVideoListBase>[0]> & { signal?: AbortSignal } = {}) {
  return fetchVideoListBase({ current, pageSize, ...rest }, signal)
}

export function fetchVideoHistoryList({
  signal,
  current = 1,
  pageSize = 30,
}: { signal?: AbortSignal; current?: number; pageSize?: number } = {}) {
  return fetchVideoListBase({ watch_history: TRUE, current, pageSize }, signal)
}

export function fetchVideoFavouriteList({
  signal,
  current = 1,
  pageSize = 30,
}: { signal?: AbortSignal; current?: number; pageSize?: number } = {}) {
  return fetchVideoListBase({ like_history: TRUE, current, pageSize }, signal)
}

export function fetchVideoCollectionList({
  signal,
  current = 1,
  pageSize = 30,
}: { signal?: AbortSignal; current?: number; pageSize?: number } = {}) {
  return fetchVideoListBase({ favorite_history: TRUE, current, pageSize }, signal)
}

export function fetchVideoBuyList({
  signal,
  current = 1,
  pageSize = 30,
}: { signal?: AbortSignal; current?: number; pageSize?: number } = {}) {
  return fetchVideoListBase({ buy_history: TRUE, current, pageSize }, signal)
}

export function fetchVideoListByWords({
  signal,
  title,
  current = 1,
  pageSize = 30,
}: { signal?: AbortSignal; title?: string; current?: number; pageSize?: number } = {}) {
  return fetchVideoListBase({ title, current, pageSize }, signal)
}

export function fetchVideoTotalQty({
  signal,
  current = 1,
  pageSize = 500,
  ...rest
}: Partial<Parameters<typeof fetchVideoListBase>[0]> & { signal?: AbortSignal } = {}) {
  return fetchVideoListBase({ only_count: TRUE, current, pageSize, ...rest }, signal)
}

export function fetchVideoTagList() {
  return fetchVideoTagListBase({ current: 1, pageSize: 500 })
}

export function fetchVideoTagListWithParam({
  type,
  promote,
  signal,
}: { type?: number | string; promote?: number | string; signal?: AbortSignal } = {}) {
  return fetchVideoTagListBase(
    {
      current: 1,
      pageSize: 500,
      ...(promote != null ? { promote: Number(promote) } : {}),
      ...(type != null ? { type: Number(type) } : {}),
    },
    signal
  )
}

export function fetchVideoTopicList({ promote, signal }: { promote?: number | string; signal?: AbortSignal } = {}) {
  return fetchVideoTopicListBase(
    { current: 1, pageSize: 500, ...(promote != null ? { promote: Number(promote) } : {}) },
    signal
  )
}

export function fetchActorList() {
  return fetchActorListBase({ current: 1, pageSize: 500 })
}

export function fetchActorInfoList({
  signal,
  current = 1,
  pageSize = 30,
  ...rest
}: Partial<Parameters<typeof fetchActorListBase>[0]> & { signal?: AbortSignal } = {}) {
  return fetchActorListBase({ current, pageSize, ...rest }, signal)
}

export function fetchProducerList({ id }: { id?: number | string } = {}) {
  return fetchProducerListBase({ current: 1, pageSize: 500, ...(id ? { id: String(id) } : {}) })
}

export function fetchProducerInfoList({
  signal,
  current = 1,
  pageSize = 30,
  ...rest
}: Partial<Parameters<typeof fetchProducerListBase>[0]> & { signal?: AbortSignal } = {}) {
  return fetchProducerListBase({ current, pageSize, ...rest }, signal)
}

export function fetchProductList({
  current = 1,
  pageSize = 100,
  product_type,
  signal,
}: { current?: number; pageSize?: number; product_type?: number; signal?: AbortSignal } = {}) {
  return fetchProductListBase({ current, pageSize, product_type, status: ENABLED }, signal)
}

export async function fetchRegionList(): Promise<any> {
  const result = await fetchRegionListBase({ current: 1, pageSize: 500 })
  return (result as any).data
}

export function fetchAdList({
  signal,
  siteType = null,
  start_status = 1,
  expire_status = 1,
  current = 1,
  pageSize = 500,
}: {
  signal?: AbortSignal
  siteType?: number | null
  start_status?: number
  expire_status?: number
  current?: number
  pageSize?: number
} = {}) {
  return fetchAdListBase({ site_type: siteType ?? undefined, start_status, expire_status, current, pageSize }, signal)
}

export function fetchPostAdEvent({
  date,
  material_id,
  material_name,
  oholo_site_id,
  oholoer_id,
  oholoer_name,
  oholoer_link,
}: {
  date: string
  material_id: number | string
  material_name: string
  oholo_site_id: number | string
  oholoer_id: number | string
  oholoer_name?: string
  oholoer_link?: string
}) {
  return postEventOholo({
    data: [
      {
        date,
        material_id: Number(material_id),
        material_name,
        oholo_site_id: Number(oholo_site_id),
        oholo_site_name: '',
        oholoer_id: Number(oholoer_id),
        oholoer_name,
        oholoer_link,
        count: 1,
      },
    ],
  })
}

export function fetchAnnouncement({ signal }: { signal?: AbortSignal } = {}) {
  return fetchAnnouncementBase({ only_count: FALSE, device_type: 'cookie', current: 1, pageSize: 500 }, signal)
}

export function fetchMemberAnnounceList({ signal }: { signal?: AbortSignal } = {}) {
  return v1MemberAnnounceList({ device_type: 'cookie', current: 1, pageSize: 100 }, signal)
}

export function fetchShortList({
  signal,
  current = 1,
  pageSize = 500,
  ...rest
}: Partial<Parameters<typeof fetchShortListBase>[0]> & { signal?: AbortSignal } = {}) {
  return fetchShortListBase({ current, pageSize, ...rest }, signal)
}

export function fetchShortTotalQty({
  signal,
  current = 1,
  pageSize = 500,
  ...rest
}: Partial<Parameters<typeof fetchShortListBase>[0]> & { signal?: AbortSignal } = {}) {
  return fetchShortListBase({ only_count: TRUE, current, pageSize, ...rest }, signal)
}

export function fetchShortFavouriteList({
  signal,
  current = 1,
  pageSize = 30,
}: { signal?: AbortSignal; current?: number; pageSize?: number } = {}) {
  return fetchShortListBase({ like_history: TRUE, current, pageSize }, signal)
}

export function fetchShortCollectionList({
  signal,
  current = 1,
  pageSize = 30,
}: { signal?: AbortSignal; current?: number; pageSize?: number } = {}) {
  return fetchShortListBase({ favorite_history: TRUE, current, pageSize }, signal)
}

export function fetchShortBuyList({
  signal,
  current = 1,
  pageSize = 30,
}: { signal?: AbortSignal; current?: number; pageSize?: number } = {}) {
  return fetchShortListBase({ buy_history: TRUE, current, pageSize }, signal)
}

export function fetchShortHistoryList({
  signal,
  current = 1,
  pageSize = 30,
}: { signal?: AbortSignal; current?: number; pageSize?: number } = {}) {
  return fetchShortListBase({ watch_history: TRUE, current, pageSize }, signal)
}

export function fetchShortListByWords({
  signal,
  title,
  current = 1,
  pageSize = 30,
}: { signal?: AbortSignal; title?: string; current?: number; pageSize?: number } = {}) {
  return fetchShortListBase({ title, current, pageSize }, signal)
}

export function fetchUploaderList({
  signal,
  current = 1,
  pageSize = 30,
  ...rest
}: Partial<Parameters<typeof fetchUploaderListBase>[0]> & { signal?: AbortSignal } = {}) {
  return fetchUploaderListBase({ current, pageSize, ...rest }, signal)
}

export function getLandingDomains({
  signal,
  current = 1,
  pageSize = 5,
  ...rest
}: Partial<Parameters<typeof getLandingDomainsBase>[0]> & { signal?: AbortSignal } = {}) {
  return getLandingDomainsBase({ current, pageSize, ...rest }, signal)
}

export function fetchCooperation({ signal }: { signal?: AbortSignal } = {}) {
  return fetchCooperationBase({ current: 1, pageSize: 500 }, signal)
}

export function fetchComicList({
  signal,
  current = 1,
  pageSize = 500,
  ...rest
}: Partial<Parameters<typeof fetchComicListBase>[0]> & { signal?: AbortSignal } = {}) {
  return fetchComicListBase({ current, pageSize, ...rest }, signal)
}

export function fetchComicTotalQty({
  signal,
  current = 1,
  pageSize = 500,
  ...rest
}: Partial<Parameters<typeof fetchComicListBase>[0]> & { signal?: AbortSignal } = {}) {
  return fetchComicListBase({ only_count: TRUE, current, pageSize, ...rest }, signal)
}

export function fetchComicCollectionList({
  signal,
  current = 1,
  pageSize = 30,
}: { signal?: AbortSignal; current?: number; pageSize?: number } = {}) {
  return fetchComicListBase({ favorite_history: TRUE, current, pageSize }, signal)
}

export function fetchComicBuyList({
  signal,
  current = 1,
  pageSize = 30,
}: { signal?: AbortSignal; current?: number; pageSize?: number } = {}) {
  return fetchComicListBase({ buy_history: TRUE, current, pageSize }, signal)
}

export function fetchComicHistoryList({
  signal,
  current = 1,
  pageSize = 30,
}: { signal?: AbortSignal; current?: number; pageSize?: number } = {}) {
  return fetchComicListBase({ view_history: TRUE, current, pageSize }, signal)
}

export function fetchComicListByWords({
  signal,
  title,
  current = 1,
  pageSize = 30,
}: { signal?: AbortSignal; title?: string; current?: number; pageSize?: number } = {}) {
  return fetchComicListBase({ title, current, pageSize }, signal)
}

export function fetchComicChapters({
  signal,
  current = 1,
  pageSize = 30,
  ...rest
}: Partial<Parameters<typeof fetchComicChaptersBase>[0]> & { signal?: AbortSignal } = {}) {
  return fetchComicChaptersBase({ current, pageSize, ...rest }, signal)
}

export function fetchNovelList({
  signal,
  current = 1,
  pageSize = 500,
  ...rest
}: Partial<Parameters<typeof fetchNovelListBase>[0]> & { signal?: AbortSignal } = {}) {
  return fetchNovelListBase({ current, pageSize, ...rest }, signal)
}

export function fetchNovelTotalQty({
  signal,
  current = 1,
  pageSize = 500,
  ...rest
}: Partial<Parameters<typeof fetchNovelListBase>[0]> & { signal?: AbortSignal } = {}) {
  return fetchNovelListBase({ only_count: TRUE, current, pageSize, ...rest }, signal)
}

export function fetchNovelCollectionList({
  signal,
  current = 1,
  pageSize = 30,
}: { signal?: AbortSignal; current?: number; pageSize?: number } = {}) {
  return fetchNovelListBase({ favorite_history: TRUE, current, pageSize }, signal)
}

export function fetchNovelBuyList({
  signal,
  current = 1,
  pageSize = 30,
}: { signal?: AbortSignal; current?: number; pageSize?: number } = {}) {
  return fetchNovelListBase({ buy_history: TRUE, current, pageSize }, signal)
}

export function fetchNovelHistoryList({
  signal,
  current = 1,
  pageSize = 30,
}: { signal?: AbortSignal; current?: number; pageSize?: number } = {}) {
  return fetchNovelListBase({ view_history: TRUE, current, pageSize }, signal)
}

export function fetchNovelListByWords({
  signal,
  title,
  current = 1,
  pageSize = 30,
}: { signal?: AbortSignal; title?: string; current?: number; pageSize?: number } = {}) {
  return fetchNovelListBase({ title, current, pageSize }, signal)
}

export function fetchNovelChapters({
  signal,
  current = 1,
  pageSize = 30,
  ...rest
}: Partial<Parameters<typeof fetchNovelChaptersBase>[0]> & { signal?: AbortSignal } = {}) {
  return fetchNovelChaptersBase({ current, pageSize, ...rest }, signal)
}

// ─── Game endpoints (not in swagger spec) ────────────────────────────────────

export function fetchGameBalance(): Promise<any> {
  return fetchApi({ url: '/game/balance', data: {} })
}

export function fetchGameBalanceAll(): Promise<any> {
  return fetchApi({ url: '/game/balance/all', data: {} })
}

export function fetchGameBalanceThirdGame(): Promise<any> {
  return fetchApi({ url: '/game/balance/third_game' })
}

export function fetchGameList({
  current,
  pageSize,
  tpCode,
}: {
  current: number
  pageSize: number
  tpCode: string
}): Promise<any> {
  return fetchApi({ url: '/game/list', data: { current, pageSize, tp_code: tpCode } })
}

export function fetchGameListV1({ current, pageSize }: { current: number; pageSize: number }): Promise<any> {
  return fetchApi({ url: '/game/list_v1', data: { current, pageSize } })
}

export function fetchGameMarquee(): Promise<any> {
  return fetchApi({ url: '/game/marquee_banner' })
}

export function fetchGameHall(): Promise<any> {
  return fetchApi({ url: '/game/hall' })
}

export function fetchGameDepositChannel({ deviceType }: { deviceType?: number } = { deviceType: 1 }): Promise<any> {
  return fetchApi({ url: '/game/pay/deposit_channel', data: { device_type: deviceType } })
}

export function fetchGamePaymentTypeList(): Promise<any> {
  return fetchApi({ url: '/game/pay/payment_type_list', data: {} })
}

export function fetchGameDepositChannelV4({ code }: { code: string }): Promise<any> {
  return fetchApi({ url: '/game/pay/deposit_channel_v4', data: { payment_type_code: code } })
}

export function fetchGameDepositOrder({
  amount,
  name,
  paymentChannelId,
}: {
  amount: number | string
  name: string
  paymentChannelId: number | string
}): Promise<any> {
  return fetchApi({ url: '/game/pay/order', data: { amount, name, payment_channel_id: paymentChannelId } })
}

export function fetchGameWithdrawOrder({
  dateBegin,
  dateEnd,
  limit,
  page,
  remittanceType,
  status,
}: {
  dateBegin?: string
  dateEnd?: string
  limit?: number
  page?: number
  remittanceType?: number
  status?: number
}): Promise<any> {
  return fetchApi({
    url: '/game/pay/order_withdrawal',
    data: { dateBegin, dateEnd, limit, page, remittanceType, status },
  })
}

export function fetchGamePaySecurity(): Promise<any> {
  return fetchApi({ url: '/game/pay/security_v2', data: {} })
}

export function fetchGameParameters(): Promise<any> {
  return fetchApi({ url: '/game/parameter_config', data: {} })
}

export function fetchGameUserStatus(): Promise<any> {
  return fetchApi({ url: '/game/user/status', data: {} })
}

export function postGame({
  gameId,
  gameType,
  tpCode,
}: {
  gameId?: number | string
  gameType?: string
  tpCode?: string
}): Promise<{ order_id: number; urls: string[] }> {
  const payload = gameType && tpCode ? { game_id: gameId, game_type: gameType, tp_code: tpCode } : { game_id: gameId }
  return fetchApi({ url: '/game/huangiu', data: payload, method: 'POST', headers: {} })
}

export function postGamePayOrder({
  amount,
  name,
  account,
  bank,
  paymentChannelId,
}: {
  amount: number | string
  name: string
  account?: string
  bank?: string
  paymentChannelId: number | string
}): Promise<{ paymentId: string; paymentLink: string }> {
  return fetchApi({
    url: '/game/pay/order',
    data: { amount, name, account, bank, payment_channel_id: paymentChannelId },
    method: 'POST',
    headers: {},
  })
}

export function postGamePayWithdraw({
  amount,
  pin,
  remittanceType,
}: {
  amount: number | string
  pin: string
  remittanceType: number
}): Promise<{ data: boolean }> {
  return fetchApi({
    url: '/game/pay/order_withdrawal',
    data: { amount, pin, remittance_type: remittanceType },
    method: 'POST',
    headers: {},
  })
}

export function patchGamePayPin({ pin }: { pin: string }): Promise<any> {
  return fetchApi({ url: '/game/pay/pin', data: { pin }, method: 'PATCH', headers: {} })
}

export function postGamePinCheck({ pin }: { pin: string }): Promise<any> {
  return fetchApi({ url: '/game/pay/pin_check', data: { pin }, method: 'POST', headers: {} })
}

export function postGamePaySecurity({
  account,
  bankName,
  branchName,
  legalName,
  remittanceType,
}: {
  account: string
  bankName: string
  branchName: string
  legalName: string
  remittanceType: number
}): Promise<any> {
  return fetchApi({
    url: '/game/pay/security',
    data: {
      account,
      bank_name: bankName,
      branch_name: branchName,
      legal_name: legalName,
      remittance_type: remittanceType,
    },
    method: 'POST',
    headers: {},
  })
}

export function postMemberGameClick(): Promise<any> {
  return fetchApi({ url: '/member_game/click', data: {}, method: 'POST', headers: {} })
}

export function postTransfer({ currency }: { currency: number }): Promise<any> {
  return fetchApi({ url: '/game/pay/transfer', data: { currency }, method: 'POST', headers: {} })
}

export function postTransferV2({ currency, tpCode }: { currency: number; tpCode: string }): Promise<any> {
  return fetchApi({ url: '/game/pay/transfer_v2', data: { currency, tp_code: tpCode }, method: 'POST', headers: {} })
}

export function fetchUsdtExchangeRate(): Promise<{ code: string; data: object }> {
  return fetchApi({ url: '/game/pay/usdt_exchange_rate', data: {}, method: 'POST' })
}

export function fetchGameDepositChannelDetail({
  channelId,
}: {
  channelId: number
}): Promise<{ code: string; data: object }> {
  return fetchApi({ url: '/game/pay/channel_details', data: { channel_id: channelId } })
}

export function postGameCompanyDeposit({
  amount,
  channelId,
  remark,
}: {
  amount: string
  channelId: number
  remark: string
}): Promise<any> {
  return fetchApi({
    url: '/game/company/order_deposit',
    data: { amount, channel_Id: channelId, remark },
    method: 'POST',
  })
}

export function fetchGamePlatformList({ signal }: { signal?: AbortSignal } = {}): Promise<{
  count: number
  data: { name: string; tp_code: string }[]
}> {
  return fetchApi({ url: '/game/platform_list_v2', method: 'GET', data: {}, signal })
}
